import React, { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ClipboardPaste, ImagePlus, Trash2, Wand2 } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Field, Input, Modal, Select, Textarea } from "../ui";
import { blockLabel, parsePaste, slugify } from "../../lib/blogImport";

const SAMPLE = `Title: 10 Natural Ways to Support Healthy Digestion
Excerpt: A practical look at the everyday habits that support comfortable digestion.
Focus Keyword: healthy digestion
Category: Nutrition
Author: ReHealthMe
Tags: digestion, gut health
---
Opening paragraph goes here.

## What affects digestion?

- First point
- Second point

[[tip: Small daily habit]]
Drink a glass of water 20 minutes before each meal.
[[/tip]]

## FAQ

Q: Is bloating always a problem?
A: Occasional bloating is common.`;

// Paste an article written in Notion / ChatGPT and it becomes a structured draft.
// Parsing is 100% local — no AI, no credits.
export default function QuickBlog({ mode = "create", onClose, onCreated, onAppend }) {
    const fileRef = useRef(null);
    const [step, setStep] = useState("paste");
    const [raw, setRaw] = useState({ text: "", html: "" });
    const [parsed, setParsed] = useState(null);
    const [form, setForm] = useState(null);
    const [images, setImages] = useState([]);
    const [busy, setBusy] = useState(false);

    const { data: meta } = useQuery({ queryKey: ["blog-meta-crm"], queryFn: async () => (await rhm.get("/blog/meta")).data });

    const matched = useMemo(() => {
        if (!parsed || !meta) return { category: null, author: null };
        const norm = (s) => (s || "").trim().toLowerCase();
        return {
            category: (meta.categories || []).find((c) => norm(c.name) === norm(parsed.meta.category_name)) || null,
            author: (meta.authors || []).find((a) => norm(a.name) === norm(parsed.meta.author_name)) || null,
        };
    }, [parsed, meta]);

    const convert = () => {
        const result = parsePaste(raw);
        if (!result.meta.title && !result.blocks.length) {
            toast.error("Nothing to convert — paste your article first");
            return;
        }
        setParsed(result);
        setForm({
            ...result.meta,
            category_id: "",
            author_id: "",
        });
        setStep("review");
    };

    // keep the real clipboard HTML so Notion / Docs formatting survives
    const onPaste = (e) => {
        const html = e.clipboardData?.getData("text/html") || "";
        const text = e.clipboardData?.getData("text/plain") || "";
        if (!html && !text) return;
        e.preventDefault();
        setRaw({ text: (raw.text ? `${raw.text}\n\n` : "") + text, html: raw.text ? "" : html });
    };

    const upload = async (files) => {
        setBusy(true);
        try {
            const added = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await rhm.post("/site/media?folder=Blog", fd);
                added.push({ url: data.url, alt: data.alt || "", caption: "", place: "end" });
            }
            setImages((list) => {
                const next = [...list, ...added];
                return next.map((im, i) => (i === 0 && !parsedHasFeatured() ? { ...im, place: "featured" } : im));
            });
            toast.success(added.length > 1 ? `${added.length} images uploaded` : "Image uploaded");
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };
    const parsedHasFeatured = () => !!(form?.featured_image?.url || parsed?.meta?.featured_image?.url);

    const composedBlocks = () => {
        const blocks = (parsed.blocks || []).map((b) => ({ ...b }));
        const body = images.filter((im) => im.place !== "featured");
        const toBlock = (im) => ({ id: `blk_${Math.random().toString(36).slice(2, 10)}`, type: "image",
            url: im.url, alt: im.alt, caption: im.caption, align: "center", width: "full", link: "", new_tab: false });
        // insert from the end so earlier indexes stay valid
        const atEnd = body.filter((im) => im.place === "end");
        const atTop = body.filter((im) => im.place === "top");
        const after = body.filter((im) => im.place !== "end" && im.place !== "top");
        after.slice().reverse().forEach((im) => {
            const idx = blocks.findIndex((b) => b.id === im.place);
            if (idx >= 0) blocks.splice(idx + 1, 0, toBlock(im));
            else blocks.push(toBlock(im));
        });
        atTop.slice().reverse().forEach((im) => blocks.unshift(toBlock(im)));
        atEnd.forEach((im) => blocks.push(toBlock(im)));
        return blocks;
    };

    const featured = () => {
        const picked = images.find((im) => im.place === "featured");
        if (picked) return { url: picked.url, alt: picked.alt || form.title, caption: picked.caption || "" };
        return form.featured_image?.url ? form.featured_image : null;
    };

    const create = async () => {
        setBusy(true);
        try {
            const blocks = composedBlocks();
            if (mode === "append") {
                onAppend(blocks);
                toast.success(`${blocks.length} blocks added to the article`);
                onClose();
                return;
            }
            const { data: post } = await rhm.post("/blog/posts", {
                title: form.title,
                category_id: form.category_id || matched.category?.id || "",
                author_id: form.author_id || matched.author?.id || "",
            });
            const payload = {
                slug: form.slug || slugify(form.title),
                excerpt: form.excerpt || "",
                tags: form.tags || [],
                style: form.style || "standard",
                blocks,
                seo: {
                    seo_title: form.seo_title || "", meta_description: form.meta_description || "",
                    focus_keyword: form.focus_keyword || "", canonical_url: form.canonical_url || "",
                    noindex: false, og_title: "", og_description: "",
                },
                product_slugs: form.product_slugs || [],
                consultation_area: form.consultation_area || "",
            };
            const fi = featured();
            if (fi) payload.featured_image = fi;
            if (form.reviewed_by) {
                payload.review = { status: "reviewed", reviewer: form.reviewed_by, reviewed_at: form.review_date || "" };
            }
            await rhm.put(`/blog/posts/${post.id}`, payload);
            toast.success("Draft created from your paste");
            onCreated(post.id);
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    // ---------------------------------------------------------------- paste step
    if (step === "paste") {
        return (
            <Modal open onClose={onClose} wide testId="quick-blog"
                title={mode === "append" ? "Paste content into this article" : "Quick blog — paste your article"}>
                <p className="text-sm text-zinc-400">
                    Paste the whole article from Notion, ChatGPT, Word or Google Docs. Headings, lists, quotes, tables,
                    images, tip boxes, product cards and FAQs are detected automatically. Nothing is sent to an AI —
                    this costs you no credits.
                </p>

                <div className="mt-4">
                    <Textarea rows={14} value={raw.text} onPaste={onPaste} data-testid="quick-blog-input"
                        onChange={(e) => setRaw({ text: e.target.value, html: "" })}
                        placeholder={SAMPLE} className="font-mono text-[12px] leading-relaxed" />
                </div>

                <div className="mt-4 border border-white/10 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="mono text-[10px] tracking-[0.18em] text-zinc-400">IMAGES (OPTIONAL)</div>
                            <p className="text-xs text-zinc-500 mt-1">
                                Upload images here if your text has no image links. The first one becomes the main
                                image; the rest can be placed between paragraphs on the next screen.
                            </p>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="quick-blog-file"
                            onChange={(e) => upload([...e.target.files])} />
                        <Btn variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}
                            data-testid="quick-blog-upload">
                            <ImagePlus className="w-3.5 h-3.5 mr-1 inline" /> {busy ? "UPLOADING…" : "UPLOAD IMAGES"}
                        </Btn>
                    </div>
                    {!!images.length && (
                        <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
                            {images.map((im, i) => (
                                <img key={i} src={im.url} alt="" className="w-full h-16 object-cover border border-white/10" />
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                    <button onClick={() => setRaw({ text: SAMPLE, html: "" })} data-testid="quick-blog-sample"
                        className="mono text-[10px] tracking-[0.16em] text-zinc-500 hover:text-gold">
                        FILL AN EXAMPLE
                    </button>
                    <div className="flex gap-2">
                        <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                        <Btn onClick={convert} disabled={!raw.text.trim()} data-testid="quick-blog-convert">
                            <Wand2 className="w-3.5 h-3.5 mr-1 inline" /> CONVERT
                        </Btn>
                    </div>
                </div>
            </Modal>
        );
    }

    // ---------------------------------------------------------------- review step
    const placeOptions = [
        { value: "featured", label: "Use as the main image" },
        { value: "top", label: "Top of the article" },
        ...(parsed.blocks || []).map((b) => ({ value: b.id, label: `After: ${blockLabel(b).slice(0, 46)}` })),
        { value: "end", label: "End of the article" },
    ];
    const flags = [
        ...parsed.warnings,
        ...(parsed.meta.category_name && !matched.category
            ? [`Category “${parsed.meta.category_name}” is not in your CRM — pick one below.`] : []),
        ...(parsed.meta.author_name && !matched.author
            ? [`Author “${parsed.meta.author_name}” is not in your CRM — pick one below.`] : []),
        ...(!featured() ? ["No main image yet — upload one or add it in the editor."] : []),
    ];

    return (
        <Modal open onClose={onClose} wide testId="quick-blog-review" title="Here's what I found">
            <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-3">
                    <Field label="Title" required>
                        <Input value={form.title} data-testid="review-title"
                            onChange={(e) => setForm({ ...form, title: e.target.value, slug: slugify(e.target.value) })} />
                    </Field>
                    <Field label="URL" hint={`rehealthme.com/blog/${form.slug || ""}`}>
                        <Input value={form.slug} data-testid="review-slug"
                            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
                    </Field>
                    <Field label="Excerpt" hint="Leave empty and it simply won't show on the article">
                        <Textarea rows={2} value={form.excerpt} data-testid="review-excerpt"
                            onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category" hint={matched.category ? "Matched from your paste" : "Pick one"}>
                            <Select value={form.category_id || matched.category?.id || ""} data-testid="review-category"
                                onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                                <option value="">Choose later</option>
                                {(meta?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </Select>
                        </Field>
                        <Field label="Author" hint={matched.author ? "Matched from your paste" : "Pick one"}>
                            <Select value={form.author_id || matched.author?.id || ""} data-testid="review-author"
                                onChange={(e) => setForm({ ...form, author_id: e.target.value })}>
                                <option value="">Choose later</option>
                                {(meta?.authors || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <Field label="Primary SEO keyword">
                        <Input value={form.focus_keyword} data-testid="review-keyword"
                            onChange={(e) => setForm({ ...form, focus_keyword: e.target.value })} />
                    </Field>
                    <Field label="SEO title" hint={`${(form.seo_title || "").length} characters`}>
                        <Input value={form.seo_title} data-testid="review-seo-title"
                            onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
                    </Field>
                    <Field label="Meta description" hint={`${(form.meta_description || "").length} / 160 characters — suggested from your text`}>
                        <Textarea rows={3} value={form.meta_description} data-testid="review-meta"
                            onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
                    </Field>
                    <Field label="Article style">
                        <Select value={form.style} data-testid="review-style"
                            onChange={(e) => setForm({ ...form, style: e.target.value })}
                            options={(meta?.styles || []).map((s) => ({ value: s.key, label: s.label }))} />
                    </Field>
                    {!!(form.tags || []).length && (
                        <div className="flex flex-wrap gap-2">
                            {form.tags.map((t) => <Badge key={t} tone="muted">{t}</Badge>)}
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    {!!flags.length && (
                        <div className="border border-orange-400/40 bg-orange-500/[0.07] p-3 space-y-1.5"
                            data-testid="review-flags">
                            {flags.map((f, i) => (
                                <div key={i} className="flex gap-2 text-xs text-orange-200">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {f}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border border-white/10">
                        <div className="px-3 py-2 border-b border-white/10 mono text-[10px] tracking-[0.18em] text-zinc-400">
                            DETECTED CONTENT · {parsed.blocks.length} BLOCKS
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-white/5" data-testid="review-blocks">
                            {parsed.blocks.map((b) => (
                                <div key={b.id} className="px-3 py-2 text-xs text-zinc-300 flex items-start gap-2">
                                    <span className="mono text-[9px] text-gold shrink-0 mt-0.5">
                                        {b.type.toUpperCase().slice(0, 5)}
                                    </span>
                                    <span className="min-w-0 truncate">{blockLabel(b)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {!!images.length && (
                        <div className="border border-white/10 p-3 space-y-3" data-testid="review-images">
                            <div className="mono text-[10px] tracking-[0.18em] text-zinc-400">
                                WHERE SHOULD EACH IMAGE GO?
                            </div>
                            {images.map((im, i) => (
                                <div key={i} className="flex gap-3 items-start">
                                    <img src={im.url} alt="" className="w-16 h-16 object-cover border border-white/10 shrink-0" />
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <Select value={im.place} data-testid={`review-image-place-${i}`}
                                            onChange={(e) => setImages(images.map((x, j) => (j === i
                                                ? { ...x, place: e.target.value } : x)))}
                                            options={placeOptions} />
                                        <Input value={im.alt} placeholder="ALT text" data-testid={`review-image-alt-${i}`}
                                            onChange={(e) => setImages(images.map((x, j) => (j === i
                                                ? { ...x, alt: e.target.value } : x)))} />
                                    </div>
                                    <button onClick={() => setImages(images.filter((_, j) => j !== i))}
                                        data-testid={`review-image-remove-${i}`}
                                        className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    <p className="text-xs text-zinc-500">
                        You can move any image or block up and down in the editor after this, and empty fields are
                        simply not shown on the published article.
                    </p>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
                <Btn variant="ghost" onClick={() => setStep("paste")} data-testid="review-back">
                    <ClipboardPaste className="w-3.5 h-3.5 mr-1 inline" /> BACK TO PASTE
                </Btn>
                <div className="flex gap-2">
                    <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                    <Btn disabled={!form.title.trim() || busy} data-testid="review-create"
                        onClick={() => create()}>
                        {mode === "append" ? "ADD TO ARTICLE" : "CREATE DRAFT"}
                    </Btn>
                </div>
            </div>
        </Modal>
    );
}
